import { InfiniteList, useScrollParent, Link, Stack, Heading, Paragraph } from '@sixfold/common-ui';
import { Localized } from '@sixfold/localization-component';
import { isNil } from '@sixfold/typed-primitives';
import React from 'react';

import { CompanyUser } from '../../../lib/graphql';
import { UserInCompany } from '../../entities';
import { UsersListItem } from '../company_users_item';

import * as styles from './styles.css';

export const CompanyUsersList: React.FunctionComponent<
  React.PropsWithChildren<{
    users: CompanyUser[];
    removeUserFromCompany: (user: UserInCompany) => Promise<void>;
    changeUserToCompanyAdmin: (user: UserInCompany) => Promise<void>;
    isViewingUserAdmin: boolean;
    isAllowedToAddOrRemoveUsers: boolean;
    hasNextPage: boolean;
    loadNextPage: () => Promise<void>;
    onClearSearch?: () => void;
  }>
> = ({
  users,
  removeUserFromCompany,
  changeUserToCompanyAdmin,
  isViewingUserAdmin,
  isAllowedToAddOrRemoveUsers,
  hasNextPage,
  loadNextPage,
  onClearSearch,
}) => {
  const getItemKey = React.useCallback((_: unknown, user: CompanyUser) => user.profile.user_id, []);

  const renderItem = React.useCallback(
    ({ item: { role, profile } }: { item: CompanyUser }) => (
      <UsersListItem
        role={role}
        profile={profile}
        removeUserFromCompany={removeUserFromCompany}
        changeUserToCompanyAdmin={changeUserToCompanyAdmin}
        isViewingUserAdmin={isViewingUserAdmin}
        isAllowedToAddOrRemoveUsers={isAllowedToAddOrRemoveUsers}
      />
    ),
    [removeUserFromCompany, changeUserToCompanyAdmin, isViewingUserAdmin, isAllowedToAddOrRemoveUsers],
  );

  const scrollParent = useScrollParent();
  if (isNil(users) || users.length === 0) {
    return (
      <Stack direction="column" alignItems="center" justifyContent="center" className={styles.emptyState}>
        <Heading type="title" size="medium">
          <Localized id="company.users.emptySearchResult.title">No users found matching your filter criteria</Localized>
        </Heading>
        <Paragraph size="medium">
          <Localized id="company.users.emptySearchResult.description">
            Please adjust or <ClearLink onClick={onClearSearch}>clear</ClearLink> the selected filters.
          </Localized>
        </Paragraph>
      </Stack>
    );
  }

  return (
    <InfiniteList
      data={users}
      itemKey={getItemKey}
      itemContent={renderItem}
      loadMoreData={hasNextPage ? loadNextPage : undefined}
      scrollParent={scrollParent}
      gap="small"
    />
  );
};

interface ClearLinkProps {
  onClick?: () => void;
  children: React.ReactNode;
}

const ClearLink = ({ onClick, children }: ClearLinkProps) => (
  <Link as="button" onClick={onClick}>
    {children}
  </Link>
);
